import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { getApi } from '../../Services/apiService';
import { AdminStatCard, AdminBadge, statusToBadge } from '../components/AdminUi';

const defaultOverview = {
  total_revenue: 0,
  monthly_revenue: 0,
  tenant_screening_revenue: 0,
  active_tenants: 0,
  total_tenants: 0,
  active_landlords: 0,
  total_landlords: 0,
  revenue_chart: { monthly: [], daily: [] },
  recent_transactions: [],
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const defaultRevenueChart = { monthly: [], daily: [] };

function normalizeRevenueChart(revenueChart) {
  if (!revenueChart) return defaultRevenueChart;
  if (Array.isArray(revenueChart)) {
    return { monthly: revenueChart, daily: [] };
  }
  return {
    monthly: revenueChart.monthly ?? [],
    daily: revenueChart.daily ?? [],
  };
}

function getDailyChartData(daily) {
  return (daily ?? []).map((item) => ({
    ...item,
    label: String(item.day),
  }));
}

const MONTH_OPTIONS = MONTH_SHORT.map((short, index) => ({
  value: index + 1,
  label: new Date(2000, index, 1).toLocaleString('en-US', { month: 'long' }),
  short,
}));

function formatChartYAxis(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0';
  if (num >= 1000) return `$${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}k`;
  return `$${num.toLocaleString()}`;
}

function formatTransactionType(type) {
  if (!type) return '';
  return String(type)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function DashboardPage() {
  const [overview, setOverview] = useState(defaultOverview);
  const [revenueChart, setRevenueChart] = useState(defaultRevenueChart);
  const [chartMonth, setChartMonth] = useState(() => new Date().getMonth() + 1);
  const [isLoading, setIsLoading] = useState(true);
  const [isChartLoading, setIsChartLoading] = useState(false);
  const overviewLoadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const fetchOverview = async () => {
      setIsLoading(true);
      try {
        const response = await getApi('/admin/dashboard/overview', {
          params: { month: chartMonth },
        });
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          const data = response.data;
          setOverview({
            ...defaultOverview,
            ...data,
            revenue_chart: normalizeRevenueChart(data.revenue_chart),
            recent_transactions: data.recent_transactions ?? [],
          });
          setRevenueChart(normalizeRevenueChart(data.revenue_chart));

          if (data.current_month) {
            const monthIndex = MONTH_SHORT.indexOf(data.current_month);
            if (monthIndex >= 0) {
              setChartMonth(monthIndex + 1);
            }
          }
          overviewLoadedRef.current = true;
        } else {
          toast.error(response?.message || 'Failed to load dashboard overview');
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load dashboard overview';
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchOverview();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  useEffect(() => {
    if (!overviewLoadedRef.current) return;

    let cancelled = false;

    const fetchRevenueChart = async () => {
      setIsChartLoading(true);
      try {
        const response = await getApi('/admin/dashboard/overview', {
          params: { month: chartMonth },
        });
        if (cancelled) return;

        if (response?.statusCode === 200 && response?.data) {
          setRevenueChart(normalizeRevenueChart(response.data.revenue_chart));
        } else {
          toast.error(response?.message || 'Failed to load revenue chart');
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error?.response?.data?.message || error?.message || 'Failed to load revenue chart';
          toast.error(message);
        }
      } finally {
        if (!cancelled) {
          setIsChartLoading(false);
        }
      }
    };

    fetchRevenueChart();
    return () => {
      cancelled = true;
    };
  }, [chartMonth]);

  const recentTransactions = (overview.recent_transactions ?? []).slice(0, 5);
  const dailyChartData = getDailyChartData(revenueChart.daily);
  const selectedMonthLabel = MONTH_OPTIONS[chartMonth - 1]?.label ?? '';

  if (isLoading) {
    return (
      <div className="dashboard-page admin-page">
        <p style={{ margin: 0, color: '#7c7e93' }}>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page admin-page">
      <div className="dashboard-stats admin-dashboard-stats">
        <AdminStatCard
          label="Total Revenue"
          value={`$${overview.total_revenue.toLocaleString()}`}
          icon="/assets/images/Stripe.svg"
          subtitle="All time"
        />
        <AdminStatCard
          label="Monthly Revenue"
          value={`$${overview.monthly_revenue.toLocaleString()}`}
          icon="/assets/images/sidebardashboardIcon.svg"
          subtitle={
            overview.current_month
              ? `${overview.current_month} — $${(overview.current_month_revenue ?? 0).toLocaleString()}`
              : 'This month'
          }
        />
        <AdminStatCard
          label="Active Tenants"
          value={overview.active_tenants.toLocaleString()}
          icon="/assets/images/UsersTenantDetail.svg"
          subtitle={`of ${overview.total_tenants} total`}
        />
        <AdminStatCard
          label="Active Landlords"
          value={overview.active_landlords.toLocaleString()}
          icon="/assets/images/sidebarPropertyIcon.svg"
          subtitle={`of ${overview.total_landlords} total`}
        />
        <AdminStatCard
          label="Tenant Screening Revenue"
          value={`$${overview.tenant_screening_revenue.toLocaleString()}`}
          icon="/assets/images/sidebardashboardIcon.svg"
          subtitle="Per screening fees"
        />
      </div>

      <div className="admin-dashboard-panels">
        <div className="dashboard-section admin-chart-card admin-dashboard-panel">
          <div className="dashboard-section-header admin-chart-section-header">
            <h2 className="dashboard-section-title">Revenue Overview</h2>
            <div className="admin-chart-filters">
              <select
                className="admin-select admin-chart-filter-select"
                value={chartMonth}
                onChange={(e) => setChartMonth(Number(e.target.value))}
                aria-label="Filter by month"
              >
                {MONTH_OPTIONS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={`admin-chart-area ${isChartLoading ? 'admin-chart-area--loading' : ''}`}>
            {isChartLoading ? (
              <p style={{ margin: 0, color: '#7c7e93', padding: '24px 0' }}>Loading chart...</p>
            ) : dailyChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#7c7e93', fontSize: 12 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis tick={{ fill: '#7c7e93', fontSize: 12 }} tickFormatter={formatChartYAxis} />
                  <Tooltip
                    labelFormatter={(label) => `${selectedMonthLabel} ${label}`}
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Revenue']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#1a5d3a" fill="#1a5d3a33" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ margin: 0, color: '#7c7e93', padding: '24px 0' }}>
                No revenue data for {selectedMonthLabel}
              </p>
            )}
          </div>
        </div>

        <div className="dashboard-section admin-dashboard-panel admin-dashboard-transactions">
          <div className="dashboard-section-header">
            <h2 className="dashboard-section-title">Recent Transactions</h2>
            <Link to="/admin/dashboard/revenue" className="dashboard-section-link">
              View all
            </Link>
          </div>
          <div className="admin-dashboard-transactions-list">
            {recentTransactions.length === 0 ? (
              <p style={{ margin: 0, color: '#7c7e93' }}>No recent transactions</p>
            ) : (
              recentTransactions.map((transaction) => (
                <div key={transaction.id || transaction.reference} className="admin-list-item">
                  <div>
                    <p style={{ margin: 0, fontWeight: 600 }}>{transaction.name}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#7c7e93' }}>
                      {transaction.description || formatTransactionType(transaction.type)}
                    </p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: 600,
                        color: transaction.amount < 0 ? '#c62828' : '#2c2c2c',
                      }}
                    >
                      {transaction.amount < 0 ? '-' : '+'}$
                      {Math.abs(transaction.amount).toLocaleString()}
                    </p>
                    <AdminBadge variant={statusToBadge(transaction.status)}>{transaction.status}</AdminBadge>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
